import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { EyeSlash, Paperclip, FilePdf, ImageSquare, FileText, X, Download } from "@phosphor-icons/react";

const ICONS = {
  "application/pdf": FilePdf,
};

export function fileIconFor(contentType) {
  if (!contentType) return FileText;
  if (contentType.startsWith("image/")) return ImageSquare;
  if (contentType === "application/pdf") return FilePdf;
  return FileText;
}

/** Composer-side chip previewing a pending upload. */
export function AttachmentChip({ file, onRemove }) {
  const Icon = fileIconFor(file.content_type);
  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-secondary text-xs" data-testid={`attachment-chip-${file.id}`}>
      <Icon size={12} weight="fill" className={file.kind === "quotation" ? "text-amber-500" : "text-primary"} />
      <span className="truncate max-w-[140px]">{file.filename}</span>
      <button onClick={onRemove} className="text-muted-foreground hover:text-foreground ml-1" data-testid={`attachment-remove-${file.id}`}>
        <X size={11} weight="bold" />
      </button>
    </div>
  );
}

/** Message-bubble side: rendered link to a file attachment. */
export function AttachmentPreview({ url }) {
  // url may be "/api/files/{id}" — prefix backend
  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = url.startsWith("http") ? url : `${backend}${url}`;
  const id = url.split("/").pop();
  const [meta, setMeta] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api.get(`/files/${id}/meta`).then((r) => { if (alive) setMeta(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [id]);

  const [revealed, setRevealed] = React.useState(false);
  const isImage = meta?.content_type?.startsWith("image/");
  const Icon = fileIconFor(meta?.content_type);
  if (meta?.moderation?.flagged && !revealed) {
    return (
      <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-md border border-destructive/40 bg-destructive/10 max-w-[280px]" data-testid={`attachment-sensitive-${id}`}>
        <EyeSlash size={20} weight="fill" className="text-destructive shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-destructive">Contenido sensible</div>
          <div className="text-[10px] text-muted-foreground truncate" title={meta.moderation.reason}>{meta.moderation.reason || "Revisado por IA"}</div>
        </div>
        <button onClick={() => setRevealed(true)} className="text-[11px] font-semibold underline shrink-0" data-testid={`attachment-reveal-${id}`}>Ver</button>
      </div>
    );
  }
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-background/60 hover:bg-background transition-colors max-w-[280px]"
      data-testid={`attachment-link-${id}`}
    >
      {isImage ? (
        <img src={fullUrl} alt={meta?.filename} className="w-10 h-10 object-cover rounded" />
      ) : (
        <Icon size={22} weight="fill" className={meta?.kind === "quotation" ? "text-amber-500" : "text-primary"} />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{meta?.filename || "Adjunto"}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
          {meta?.kind || "archivo"} · {meta ? Math.round((meta.size_bytes || 0) / 1024) : "…"} KB
        </div>
      </div>
      <Download size={14} className="text-muted-foreground shrink-0" />
    </a>
  );
}

/** Paperclip button inside the composer. */
export default function AttachmentButton({ conversationId, onUploaded }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        if (conversationId) form.append("conversation_id", conversationId);
        const { data } = await api.post("/files", form, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded?.(data);
      }
      toast.success(`${files.length} archivo(s) adjuntado(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo archivo");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
        onChange={handle}
        className="hidden"
        data-testid="attachment-file-input"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        title="Adjuntar archivo (PDF cotización SAP, imagen)"
        data-testid="btn-attach"
      >
        <Paperclip size={16} />
      </Button>
    </>
  );
}
